/**
 * Costume Design — the wardrobe department, MERGED with Characters.
 *
 * Per the 2026-06-26 yap + _GLOBAL.md G1:
 *   - The Character panel owns each character's PRIMARY look (look 0).
 *     It shows here READ-THROUGH (rendered read-only; edited on the
 *     Characters side).
 *   - THIS panel owns the SECONDARY looks (look 1..n): add / edit /
 *     remove additional costumes for a character.
 *   - A look is just NOTE + REFERENCE IMAGE. There are NO scene-
 *     assignment chips (G1): a look is applied by `@`-referencing it in
 *     the SCRIPT at scene/character granularity. The card only DEFINES
 *     the look; where it's *used* is DERIVED read-only from `@look`
 *     entity-refs and shown as a usage badge.
 *   - Every reference image gets an Edit button → the shared Photoshop-
 *     style image editor (G7). Images can be UPLOADED or GENERATED
 *     (shared `runGeneration` seam).
 *   - The per-look edit form is componentized (`buildLookForm`) so the
 *     SAME form renders inline here AND inside the shared entity edit-
 *     drawer (G6) when a `@look` ref is opened from the Script Editor
 *     (`openCostumeLookDrawer` / the `koko:open-look` event).
 *
 * Canonical model: the shared store's `costumeLooksByCharacterId`
 * (CostumeLook[]) plus `character.primaryLookId`. This panel only ever
 * mutates SECONDARY looks; it preserves the primary look untouched.
 * Legacy data persisted by the old panel into `costumeByCharacterId`
 * (a JSON-blob or plain note string) is migrated up to the new model
 * on first sight so nothing is lost.
 */

import "../styles/costume.css";

import { BasePanel } from "./BasePanel";
import {
  buildEmptyState,
  buildHelperParagraph,
  buildSectionLabel,
  buildStudioButton,
} from "./dom";
import { buildPixelIdenticonImage } from "../shared/pixelIdenticon";
import { openEntityDrawer, openImageEditor, runGeneration } from "../shared";
import type { CostumeLook, StudioCharacter, StudioStore } from "../StudioStore";

interface CostumePanelOptions {
  store: StudioStore;
}

/** Deep-link event the Script Editor dispatches when a `@look` ref is
 *  clicked: `new CustomEvent("koko:open-look", { detail: { characterId,
 *  lookId } })`. Mirrors the Characters panel's `koko:open-character`. */
const OPEN_LOOK_EVENT = "koko:open-look";

const NOTE_PLACEHOLDER =
  "e.g. Dark turtleneck under a paint-splattered denim jacket, round wire glasses, scuffed Blundstones.";

export class CostumePanel extends BasePanel {
  private readonly store: StudioStore;
  private listEl: HTMLDivElement | null = null;
  private storeUnsubscribers: (() => void)[] = [];

  /**
   * Set while WE write to the store, so the resulting store
   * notification doesn't rebuild the DOM and steal textarea focus
   * mid-keystroke. Structural changes (add/remove) intentionally do NOT
   * set this, so the count + cards re-render.
   */
  private suppressNextRender = false;

  /** Characters whose legacy `costumeByCharacterId` blob we've already
   *  migrated up to `costumeLooksByCharacterId`, so we don't loop. */
  private readonly migratedCharacterIds = new Set<string>();

  private readonly onOpenLookEvent = (event: Event) => {
    const detail = (event as CustomEvent<{ characterId?: string; lookId?: string }>)
      .detail;
    if (detail?.characterId && detail?.lookId) {
      openCostumeLookDrawer(this.store, detail.characterId, detail.lookId);
    }
  };

  constructor(options: CostumePanelOptions) {
    super();
    this.store = options.store;
  }

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");

    const box = buildPixelBoxLocal();
    box.fill.appendChild(
      buildHelperParagraph(
        "Each character carries a PRIMARY look (defined with the character, shown " +
          "read-only here). Add SECONDARY looks below — a note plus a reference " +
          "image. Apply any look to a scene by typing @ in the script; this card " +
          "just defines it.",
      ),
    );

    const list = document.createElement("div");
    list.className = "costume-sheets";
    box.fill.appendChild(list);
    this.listEl = list;

    root.appendChild(box.outer);
    return root;
  }

  protected onMounted(): void {
    for (const key of [
      "characters",
      "costumeLooksByCharacterId",
      "costumeByCharacterId",
      "entityRefs",
      "scenes",
    ] as const) {
      this.storeUnsubscribers.push(
        this.store.subscribe(key, () => this.syncAndRender()),
      );
    }
    window.addEventListener(OPEN_LOOK_EVENT, this.onOpenLookEvent);
    // `window.__studioStore` is exposed centrally from main.ts.
    this.syncAndRender();
  }

  protected onShown(): void {
    this.syncAndRender();
  }

  protected onBeforeDestroy(): void {
    for (const unsub of this.storeUnsubscribers) unsub();
    this.storeUnsubscribers = [];
    window.removeEventListener(OPEN_LOOK_EVENT, this.onOpenLookEvent);
  }

  // ------------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------------

  private syncAndRender(): void {
    if (this.suppressNextRender) {
      this.suppressNextRender = false;
      return;
    }
    // Migrating writes to the store, which re-enters here; bail so we
    // render off the migrated state rather than the pre-migration one.
    if (this.migrateLegacyLooks()) return;
    this.renderList();
  }

  /**
   * One-time per character: if the new `costumeLooksByCharacterId` slot
   * is empty but the legacy `costumeByCharacterId` string has data,
   * upgrade it into secondary looks so old wardrobes aren't lost.
   * Returns true if it wrote to the store (caller should bail + wait for
   * the re-render the write triggers).
   */
  private migrateLegacyLooks(): boolean {
    const characters = this.store.get("characters");
    const looksById = this.store.get("costumeLooksByCharacterId");
    const legacyById = this.store.get("costumeByCharacterId");
    for (const character of characters) {
      if (this.migratedCharacterIds.has(character.id)) continue;
      const existing = looksById[character.id] ?? [];
      const legacy = legacyById[character.id] ?? "";
      if (existing.length > 0 || legacy.trim() === "") {
        this.migratedCharacterIds.add(character.id);
        continue;
      }
      const migrated = parseLegacyLooks(legacy);
      this.migratedCharacterIds.add(character.id);
      if (migrated.length > 0) {
        this.store.setCostumeLooks(character.id, migrated);
        return true;
      }
    }
    return false;
  }

  private renderList(): void {
    if (!this.listEl) return;
    this.listEl.replaceChildren();
    const characters = this.store.get("characters");
    if (characters.length === 0) {
      this.listEl.appendChild(
        buildEmptyState(
          "No characters yet. Create some in the character creator, then dress them here.",
        ),
      );
      return;
    }
    characters.forEach((character, index) => {
      const sheet = this.buildSheet(character);
      sheet.style.setProperty("--stagger", String(index));
      this.listEl!.appendChild(sheet);
    });
  }

  private buildSheet(character: StudioCharacter): HTMLElement {
    const sheet = document.createElement("section");
    sheet.className = "costume-sheet";
    sheet.dataset.characterId = character.id;

    sheet.appendChild(this.buildIdentity(character));
    sheet.appendChild(this.buildSheetBody(character));
    return sheet;
  }

  private buildIdentity(character: StudioCharacter): HTMLElement {
    const rail = document.createElement("div");
    rail.className = "costume-identity";

    const avatar = buildPixelIdenticonImage(character.id, {
      alt: `${character.name} avatar`,
    });
    avatar.classList.add("costume-avatar");
    rail.appendChild(avatar);

    const name = document.createElement("div");
    name.className = "costume-identity-name studio-card-title";
    name.textContent = character.name;
    rail.appendChild(name);

    const id = document.createElement("div");
    id.className = "costume-identity-id";
    id.textContent = character.id;
    rail.appendChild(id);

    const secondary = this.secondaryLooks(character);
    const hasPrimary = !!this.primaryLook(character);
    const total = secondary.length + (hasPrimary ? 1 : 0);
    const count = document.createElement("div");
    count.className = "costume-identity-count";
    count.textContent = `${total} look${total === 1 ? "" : "s"}`;
    rail.appendChild(count);

    return rail;
  }

  private buildSheetBody(character: StudioCharacter): HTMLElement {
    const body = document.createElement("div");
    body.className = "costume-sheet-body";
    body.appendChild(this.buildPrimarySection(character));
    body.appendChild(this.buildSecondarySection(character));
    return body;
  }

  // ---- Primary look (read-through from Characters) ------------------

  private buildPrimarySection(character: StudioCharacter): HTMLElement {
    const section = document.createElement("div");
    section.className = "costume-section costume-primary-section";

    const label = buildSectionLabel("Primary look");
    label.classList.add("costume-section-label");
    section.appendChild(label);

    const primary = this.primaryLook(character);
    if (primary) {
      section.appendChild(
        buildLookForm({
          store: this.store,
          character,
          look: primary,
          variant: "primary",
          label: "Primary",
        }),
      );
    } else {
      section.appendChild(this.buildPrimaryPlaceholder(character));
    }
    return section;
  }

  private buildPrimaryPlaceholder(character: StudioCharacter): HTMLElement {
    const card = document.createElement("div");
    card.className = "costume-primary-empty";
    card.dataset.kokoAction = "costume-primary-empty";

    const text = document.createElement("p");
    text.className = "costume-primary-empty-text";
    text.textContent =
      "No primary look yet. A character's primary costume is defined on the " +
      "Characters panel — once set, it appears here read-only.";
    card.appendChild(text);

    const open = buildStudioButton("Open in Characters →", {
      variant: "secondary",
      onClick: () =>
        window.dispatchEvent(
          new CustomEvent("koko:open-character", {
            detail: { characterId: character.id },
          }),
        ),
    });
    open.dataset.kokoAction = "costume-open-character";
    open.dataset.characterId = character.id;
    card.appendChild(open);
    return card;
  }

  // ---- Secondary looks (owned here) --------------------------------

  private buildSecondarySection(character: StudioCharacter): HTMLElement {
    const section = document.createElement("div");
    section.className = "costume-section costume-secondary-section";

    const label = buildSectionLabel("Secondary looks");
    label.classList.add("costume-section-label");
    section.appendChild(label);

    const column = document.createElement("div");
    column.className = "costume-looks";

    const secondary = this.secondaryLooks(character);
    if (secondary.length === 0) {
      const empty = document.createElement("div");
      empty.className = "costume-looks-empty";
      empty.textContent =
        "No secondary looks yet. Add one to give this character a costume change.";
      column.appendChild(empty);
    } else {
      secondary.forEach((look, index) => {
        column.appendChild(
          buildLookForm({
            store: this.store,
            character,
            look,
            variant: "secondary",
            label: `Look ${lookLetter(index)}`,
            onChange: (patch) => this.persistLook(character.id, look.id, patch),
            onRemove: () => this.removeLook(character.id, look.id),
            onExpand: () =>
              openCostumeLookDrawer(this.store, character.id, look.id),
          }),
        );
      });
    }

    const addButton = buildStudioButton("+ Add look", {
      variant: "secondary",
      onClick: () => this.addLook(character.id),
    });
    addButton.classList.add("costume-add-look");
    addButton.dataset.kokoAction = "costume-add-look";
    addButton.dataset.characterId = character.id;
    column.appendChild(addButton);

    section.appendChild(column);
    return section;
  }

  // ------------------------------------------------------------------
  // Model helpers
  // ------------------------------------------------------------------

  private allLooks(character: StudioCharacter): CostumeLook[] {
    return this.store.get("costumeLooksByCharacterId")[character.id] ?? [];
  }

  /** The primary look (owned by Characters) if `primaryLookId` resolves. */
  private primaryLook(character: StudioCharacter): CostumeLook | null {
    const id = character.primaryLookId;
    if (!id) return null;
    return this.allLooks(character).find((look) => look.id === id) ?? null;
  }

  /** Every look that isn't the primary, in stored order. */
  private secondaryLooks(character: StudioCharacter): CostumeLook[] {
    const primaryId = character.primaryLookId ?? null;
    return this.allLooks(character).filter((look) => look.id !== primaryId);
  }

  // ------------------------------------------------------------------
  // Mutations (secondary looks only; the primary is preserved as-is)
  // ------------------------------------------------------------------

  private addLook(characterId: string): void {
    const looks = [
      ...(this.store.get("costumeLooksByCharacterId")[characterId] ?? []),
    ];
    looks.push({
      id: generateLookId(),
      note: "",
      referenceImage: null,
    });
    // Structural change → allow the resulting render (count + new card).
    this.store.setCostumeLooks(characterId, looks);
  }

  private removeLook(characterId: string, lookId: string): void {
    const looks = (
      this.store.get("costumeLooksByCharacterId")[characterId] ?? []
    ).filter((look) => look.id !== lookId);
    this.store.setCostumeLooks(characterId, looks);
  }

  /** Persist a field patch to one secondary look WITHOUT re-rendering
   *  (the live form already reflects the change in place). */
  private persistLook(
    characterId: string,
    lookId: string,
    patch: Partial<CostumeLook>,
  ): void {
    this.suppressNextRender = true;
    persistLookPatch(this.store, characterId, lookId, patch);
  }
}

// ====================================================================
// Componentized per-look edit form (shared by the panel + the G6 drawer)
// ====================================================================

interface LookFormParams {
  store: StudioStore;
  character: StudioCharacter;
  /** Snapshot of the look; the form keeps its own working copy. */
  look: CostumeLook;
  variant: "primary" | "secondary";
  /** Display label, e.g. "Primary" or "Look A". */
  label: string;
  /** Persist a field patch (omitted ⇒ read-only). */
  onChange?: (patch: Partial<CostumeLook>) => void;
  /** Remove this look (secondary only). */
  onRemove?: () => void;
  /** Open this look in the shared edit-drawer (inline panel only). */
  onExpand?: () => void;
}

/**
 * Build ONE look's edit card. Self-updating for its own image so the
 * parent never has to re-render on an image change (which would steal
 * textarea focus). Used inline in the panel AND inside `openEntityDrawer`.
 */
export function buildLookForm(params: LookFormParams): HTMLElement {
  const { store, character, variant, label } = params;
  const readOnly = variant === "primary" || !params.onChange;
  const working: CostumeLook = { ...params.look };

  const card = document.createElement("div");
  card.className = "costume-look";
  card.dataset.lookId = working.id;
  card.dataset.variant = variant;
  if (readOnly) card.classList.add("is-readonly");

  // ---- Figure (image + actions) ----------------------------------
  const figure = document.createElement("div");
  figure.className = "costume-look-figure";

  const imageSlot = document.createElement("div");
  imageSlot.className = "costume-look-image-slot";
  figure.appendChild(imageSlot);

  const renderImage = (): void => {
    imageSlot.replaceChildren();
    if (working.referenceImage) {
      const img = document.createElement("img");
      img.className = "costume-look-image";
      img.src = working.referenceImage;
      img.alt = `${character.name} ${label} reference`;
      imageSlot.appendChild(img);
    } else {
      const placeholder = document.createElement("div");
      placeholder.className = "costume-look-image-empty";
      placeholder.textContent = readOnly
        ? "No reference image"
        : "No reference image yet";
      imageSlot.appendChild(placeholder);
    }
  };
  renderImage();

  const setImage = (next: string | null): void => {
    working.referenceImage = next;
    renderImage();
    params.onChange?.({ referenceImage: next });
  };

  if (!readOnly) {
    figure.appendChild(buildImageActions(store, character, working, setImage));
  }

  card.appendChild(figure);

  // ---- Body (label, note, usage, actions) ------------------------
  const body = document.createElement("div");
  body.className = "costume-look-body";

  const top = document.createElement("div");
  top.className = "costume-look-top";
  const labelEl = document.createElement("span");
  labelEl.className = "costume-look-label";
  labelEl.textContent = label;
  top.appendChild(labelEl);
  if (readOnly) {
    const badge = document.createElement("span");
    badge.className = "costume-readonly-badge";
    badge.textContent = "read-only · edit on Characters";
    top.appendChild(badge);
  } else if (params.onExpand) {
    const expand = document.createElement("button");
    expand.type = "button";
    expand.className = "costume-look-expand";
    expand.title = "Open in edit drawer";
    expand.setAttribute("aria-label", `Open ${label} in edit drawer`);
    expand.textContent = "⤢";
    expand.dataset.kokoAction = "costume-expand-look";
    expand.dataset.lookId = working.id;
    expand.addEventListener("click", () => params.onExpand?.());
    top.appendChild(expand);
  }
  body.appendChild(top);

  const note = document.createElement("textarea");
  note.className = "studio-textarea costume-look-note";
  note.placeholder = NOTE_PLACEHOLDER;
  note.value = working.note;
  note.dataset.kokoAction = "costume-note";
  note.dataset.characterId = character.id;
  note.dataset.lookId = working.id;
  if (readOnly) {
    note.readOnly = true;
  } else {
    note.addEventListener("input", () => {
      working.note = note.value;
      params.onChange?.({ note: note.value });
    });
  }
  body.appendChild(note);

  body.appendChild(buildUsageRow(store, working.id));

  if (!readOnly && params.onRemove) {
    const remove = buildStudioButton("Remove look", {
      variant: "danger",
      onClick: () => params.onRemove?.(),
    });
    remove.classList.add("costume-look-remove");
    remove.dataset.kokoAction = "costume-remove-look";
    remove.dataset.lookId = working.id;
    body.appendChild(remove);
  }

  if (variant === "primary") {
    const open = buildStudioButton("Edit on Characters →", {
      variant: "secondary",
      onClick: () =>
        window.dispatchEvent(
          new CustomEvent("koko:open-character", {
            detail: { characterId: character.id },
          }),
        ),
    });
    open.classList.add("costume-look-open-character");
    open.dataset.kokoAction = "costume-open-character";
    open.dataset.characterId = character.id;
    body.appendChild(open);
  }

  card.appendChild(body);
  return card;
}

/** Upload / Generate / Edit (image editor, G7) action row. */
function buildImageActions(
  store: StudioStore,
  character: StudioCharacter,
  look: CostumeLook,
  setImage: (next: string | null) => void,
): HTMLElement {
  const actions = document.createElement("div");
  actions.className = "costume-look-figure-actions";

  // Upload via a hidden file input.
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = "image/*";
  fileInput.className = "costume-hidden-file";
  fileInput.dataset.kokoAction = "costume-upload";
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () =>
        setImage(typeof reader.result === "string" ? reader.result : null);
      reader.readAsDataURL(file);
    }
    fileInput.value = "";
  });

  const uploadButton = buildStudioButton(
    look.referenceImage ? "Replace" : "Upload",
    { variant: "secondary", onClick: () => fileInput.click() },
  );
  uploadButton.dataset.kokoAction = "costume-upload-button";

  const generateButton = buildStudioButton("Generate", {
    variant: "secondary",
    onClick: () =>
      void generateLookImage(store, character, look, generateButton, setImage),
  });
  generateButton.dataset.kokoAction = "costume-generate";

  const editButton = buildStudioButton("Edit", {
    variant: "secondary",
    onClick: () =>
      openImageEditor({
        image: look.referenceImage ?? "",
        title: `Edit ${character.name} — ${look.note ? look.note.slice(0, 32) : "look"}`,
        onChange: (next) => setImage(next),
      }),
  });
  editButton.dataset.kokoAction = "costume-edit-image";

  actions.appendChild(uploadButton);
  actions.appendChild(generateButton);
  actions.appendChild(editButton);
  actions.appendChild(fileInput);
  return actions;
}

/**
 * Generate a reference image through the shared `runGeneration` seam,
 * assembling a prompt from the character + the look note + the mood
 * board's visual direction palette. The provider is stubbed tonight, so
 * this resolves to a deterministic placeholder after a visible delay.
 */
async function generateLookImage(
  store: StudioStore,
  character: StudioCharacter,
  look: CostumeLook,
  button: HTMLButtonElement,
  setImage: (next: string | null) => void,
): Promise<void> {
  const original = button.textContent;
  button.disabled = true;
  button.textContent = "Generating…";
  try {
    const direction = store.getVisualDirection();
    const promptParts = [
      character.name,
      character.description,
      character.build,
      character.hair,
      look.note,
      "full-body costume reference, wardrobe sheet",
    ].filter((part) => part && part.trim().length > 0);
    const result = await runGeneration({
      kind: "image",
      prompt: promptParts.join(", "),
      aspect: "9:16",
      palette: direction.palette,
      seed: `${character.id}:${look.id}:${look.note}`,
    });
    if (result.url) setImage(result.url);
  } catch (error) {
    console.warn("[studio] costume generate failed:", error);
  } finally {
    button.disabled = false;
    button.textContent = original ?? "Generate";
  }
}

/**
 * Read-only "applied in" row, DERIVED from `@look` entity-refs (G1).
 * No editable chips — usage is the script's job. Shows the scenes a look
 * is referenced in, or a hint when it hasn't been applied yet.
 */
function buildUsageRow(store: StudioStore, lookId: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "costume-look-usage";
  row.dataset.lookId = lookId;

  const caption = document.createElement("span");
  caption.className = "costume-usage-caption";
  row.appendChild(caption);

  const scenes = store.get("scenes");
  const usedSceneIds = store.getScenesForEntity(lookId);
  const indices = usedSceneIds
    .map((sceneId) => scenes.findIndex((scene) => scene.id === sceneId))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);

  if (indices.length === 0) {
    caption.textContent = "Not applied yet";
    const hint = document.createElement("span");
    hint.className = "costume-usage-hint";
    hint.textContent = "— reference this look with @ in the script to apply it.";
    row.appendChild(hint);
  } else {
    caption.textContent = "Applied in";
    for (const index of indices) {
      const chip = document.createElement("span");
      chip.className = "costume-usage-chip";
      chip.textContent = `sc. ${index + 1}`;
      row.appendChild(chip);
    }
  }
  return row;
}

// ====================================================================
// Shared drawer entry-point (G6) — used by the Script Editor's @look ref
// ====================================================================

/**
 * Open one costume look in the shared entity edit-drawer (G6). The same
 * `buildLookForm` component renders inside the drawer body. Primary looks
 * open read-only (they're edited on the Characters side).
 */
export function openCostumeLookDrawer(
  store: StudioStore,
  characterId: string,
  lookId: string,
): void {
  const character = store
    .get("characters")
    .find((entry) => entry.id === characterId);
  if (!character) return;
  const looks = store.get("costumeLooksByCharacterId")[characterId] ?? [];
  const look = looks.find((entry) => entry.id === lookId);
  if (!look) return;

  const isPrimary = character.primaryLookId === lookId;
  const secondaryIndex = looks
    .filter((entry) => entry.id !== (character.primaryLookId ?? null))
    .findIndex((entry) => entry.id === lookId);
  const label = isPrimary
    ? "Primary"
    : `Look ${lookLetter(Math.max(0, secondaryIndex))}`;

  openEntityDrawer({
    title: `${character.name} — ${label}`,
    subtitle: `costume look · ${lookId}`,
    render: (body) => {
      body.appendChild(
        buildLookForm({
          store,
          character,
          look,
          variant: isPrimary ? "primary" : "secondary",
          label,
          onChange: isPrimary
            ? undefined
            : (patch) => persistLookPatch(store, characterId, lookId, patch),
        }),
      );
    },
  });
}

// ====================================================================
// Pure helpers
// ====================================================================

/** Build the standard pixel box wrapper. */
function buildPixelBoxLocal(): { outer: HTMLDivElement; fill: HTMLDivElement } {
  const outer = document.createElement("div");
  outer.className = "studio-pixel-box";
  const fill = document.createElement("div");
  fill.className = "studio-pixel-box-fill";
  outer.appendChild(fill);
  return { outer, fill };
}

/** Merge a field patch into one look and persist the full array. */
function persistLookPatch(
  store: StudioStore,
  characterId: string,
  lookId: string,
  patch: Partial<CostumeLook>,
): void {
  const looks = [...(store.get("costumeLooksByCharacterId")[characterId] ?? [])];
  const idx = looks.findIndex((look) => look.id === lookId);
  if (idx === -1) return;
  looks[idx] = { ...looks[idx], ...patch };
  store.setCostumeLooks(characterId, looks);
}

/** "A", "B", … "Z", "AA" — alphabetic look labels. */
function lookLetter(index: number): string {
  let n = index;
  let label = "";
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

function generateLookId(): string {
  return `lk_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Upgrade legacy `costumeByCharacterId` data into the new CostumeLook[].
 * Accepts the old panel's JSON envelope (`{v,looks:[{id,note,image,
 * sceneFrom,sceneTo}]}`) or a plain free-form note string (→ one look).
 */
function parseLegacyLooks(raw: string): CostumeLook[] {
  const trimmed = raw.trim();
  if (trimmed === "") return [];
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as { looks?: unknown };
      if (Array.isArray(parsed.looks)) {
        return parsed.looks
          .map((entry) => normalizeLegacyLook(entry))
          .filter((entry): entry is CostumeLook => entry !== null);
      }
    } catch {
      // Fall through to plain-note handling.
    }
  }
  return [{ id: generateLookId(), note: raw, referenceImage: null }];
}

function normalizeLegacyLook(entry: unknown): CostumeLook | null {
  if (!entry || typeof entry !== "object") return null;
  const record = entry as Record<string, unknown>;
  const image =
    typeof record.referenceImage === "string"
      ? record.referenceImage
      : typeof record.image === "string"
        ? record.image
        : null;
  const sceneFrom =
    typeof record.sceneFrom === "number" ? Math.floor(record.sceneFrom) : undefined;
  const sceneToRaw = record.sceneTo;
  const sceneTo =
    typeof sceneToRaw === "number" ? Math.floor(sceneToRaw) : undefined;
  return {
    id: typeof record.id === "string" ? record.id : generateLookId(),
    note: typeof record.note === "string" ? record.note : "",
    referenceImage: image && image.length > 0 ? image : null,
    sceneFrom,
    sceneTo,
  };
}
